/**
 * Energy Calculations - Core business logic for Hollywood Trucks unit energy metrics
 *
 * These are PRODUCTION formulas used across the application for:
 * - Energy mix breakdown (Solar, Grid, Generator percentages)
 * - CO2 emissions calculations
 * - Cost savings estimates
 * - Energy unit formatting
 *
 * Hollywood Trucks Unit Energy Model:
 * - Solar → Battery (storage) → Load (primary path)
 * - Grid: Backup when battery insufficient (< 3% of total)
 * - Generator: Rare backup (< 1% of total)
 */

import { ENERGY_RATES, ENERGY_CONVERSION } from '@/lib/energyConstants';

// Energy constants - imports centralized rates, adds calculation-specific constants
export const ENERGY_CONSTANTS = {
  // Diesel generator efficiency (from centralized constants)
  DIESEL_KWH_PER_GALLON: ENERGY_CONVERSION.DIESEL_EFFECTIVE_KWH_PER_GALLON,
  CO2_KG_PER_DIESEL_GALLON: ENERGY_CONVERSION.CO2_KG_PER_DIESEL_GALLON,

  // Cost calculations (USD) - from centralized constants
  COST_PER_KWH_SAVED: ENERGY_RATES.GRID_IMPORT_RATE_PER_KWH,
  GRID_SELL_RATE_PER_KWH: ENERGY_RATES.GRID_SELL_RATE_PER_KWH,

  // Energy distribution targets
  SOLAR_TARGET_PERCENT: 97,
  GRID_MAX_PERCENT: 3,
  GENERATOR_MAX_PERCENT: 1,

  // Grid export (selling excess solar)
  // With ~97% solar reliance, significant excess during peak sun hours
  GRID_EXPORT_BASE_PERCENT: 18,    // ~18% of PV exported on average
  GRID_EXPORT_VARIANCE: 0.15,      // ±15% variance for realism

  // Wildcard conditions (realistic unit scenarios)
  BROKEN_UNIT_PERCENT: 1,      // 1% of units have damaged panels
  BROKEN_PV_MULTIPLIER: 0.1,   // 10% of normal PV output
  SHADED_UNIT_PERCENT: 10,     // 10% of units are shaded
  SHADED_PV_MULTIPLIER: 0.5,   // 50% of normal PV output
} as const;

/**
 * Energy mix breakdown interface
 */
export interface EnergyMixResult {
  solarKwh: number;
  gridKwh: number;
  generatorKwh: number;
  solarPercent: number;
  gridPercent: number;
  generatorPercent: number;
}

/**
 * Calculate energy mix from total solar production
 * Grid and Generator are derived as small percentages of solar
 *
 * @param totalSolarKwh - Total solar energy produced (kWh)
 * @returns Energy mix breakdown with percentages
 */
export function calculateEnergyMix(totalSolarKwh: number): EnergyMixResult {
  if (totalSolarKwh <= 0) {
    return {
      solarKwh: 0,
      gridKwh: 0,
      generatorKwh: 0,
      solarPercent: 0,
      gridPercent: 0,
      generatorPercent: 0,
    };
  }

  // Grid: ~2.5% of solar (backup usage, < 3% target)
  const gridKwh = Math.round(totalSolarKwh * 0.025);

  // Generator: ~0.8% of solar (rare backup, < 1% target)
  const generatorKwh = Math.round(totalSolarKwh * 0.008);

  const total = totalSolarKwh + gridKwh + generatorKwh;

  return {
    solarKwh: totalSolarKwh,
    gridKwh,
    generatorKwh,
    solarPercent: Math.round((totalSolarKwh / total) * 100),
    gridPercent: Math.round((gridKwh / total) * 100),
    generatorPercent: Math.round((generatorKwh / total) * 100),
  };
}

/**
 * Calculate CO2 emissions reduced by using solar instead of diesel generator
 *
 * @param solarKwh - Solar energy used (kWh)
 * @returns CO2 reduced in kg
 */
export function calculateCO2Reduced(solarKwh: number): number {
  const dieselGallonsSaved = solarKwh / ENERGY_CONSTANTS.DIESEL_KWH_PER_GALLON;
  return Math.round(dieselGallonsSaved * ENERGY_CONSTANTS.CO2_KG_PER_DIESEL_GALLON * 10) / 10;
}

/**
 * Calculate cost savings from using solar instead of grid
 *
 * @param solarKwh - Solar energy used (kWh)
 * @returns Cost saved in USD
 */
export function calculateCostSaved(solarKwh: number): number {
  return Math.round(solarKwh * ENERGY_CONSTANTS.COST_PER_KWH_SAVED);
}

/**
 * Calculate revenue from selling excess energy to grid
 *
 * @param gridExportKwh - Energy sold to grid (kWh)
 * @returns Revenue in USD
 */
export function calculateGridSellRevenue(gridExportKwh: number): number {
  return Math.round(gridExportKwh * ENERGY_CONSTANTS.GRID_SELL_RATE_PER_KWH * 100) / 100;
}

/**
 * Format energy values for display
 * Uses MWh for values >= 10,000 kWh (5+ digits)
 * Keeps kWh for smaller values (e.g., 9,999 kWh stays as "9,999 kWh")
 *
 * @param kwh - Energy value in kWh
 * @returns Formatted value and unit
 */
export function formatEnergy(kwh: number): { value: string; unit: string; full: string } {
  if (kwh >= 10000) {
    const mwh = (kwh / 1000).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return { value: mwh, unit: 'MWh', full: `${mwh} MWh` };
  }
  const formatted = Math.round(kwh).toLocaleString();
  return { value: formatted, unit: 'kWh', full: `${formatted} kWh` };
}

/**
 * Determine PV output multiplier based on unit condition
 * Simulates real-world scenarios: broken panels, shading, etc.
 *
 * @param conditionRoll - Random value 0-1 to determine condition
 * @returns PV output multiplier (0.1 to 1.0)
 */
export function getPvMultiplier(conditionRoll: number): number {
  if (conditionRoll < ENERGY_CONSTANTS.BROKEN_UNIT_PERCENT / 100) {
    // 1% of units: broken/damaged → 10% PV output
    return ENERGY_CONSTANTS.BROKEN_PV_MULTIPLIER;
  }
  if (conditionRoll < (ENERGY_CONSTANTS.BROKEN_UNIT_PERCENT + ENERGY_CONSTANTS.SHADED_UNIT_PERCENT) / 100) {
    // 10% of units: shaded → 50% PV output
    return ENERGY_CONSTANTS.SHADED_PV_MULTIPLIER;
  }
  // 89% of units: normal → 100% PV output
  return 1.0;
}

/**
 * Calculate realistic grid import for a unit
 * Grid is backup when solar + battery insufficient
 *
 * @param pvKwh - Solar PV production (kWh)
 * @returns Grid import (kWh), typically < 3% of PV
 */
export function calculateGridImport(pvKwh: number): number {
  // Grid: ~2.5% of PV (minimal backup usage)
  return Math.round(pvKwh * 0.025 * 10) / 10;
}

/**
 * Calculate realistic generator usage for a unit
 * Generator is rare backup, only used by ~10% of units
 *
 * @param pvKwh - Solar PV production (kWh)
 * @param useGenerator - Whether this unit uses generator (random, ~10% chance)
 * @returns Generator usage (kWh), typically < 1% of PV
 */
export function calculateGeneratorUsage(pvKwh: number, useGenerator: boolean): number {
  if (!useGenerator) return 0;
  // Generator: ~0.8% of PV when used
  return Math.round(pvKwh * 0.008 * 10) / 10;
}

/**
 * Calculate grid export (selling excess solar to grid)
 * With ~97% solar reliance and low grid/generator usage, significant excess during peak sun hours
 *
 * @param pvKwh - Solar PV production (kWh)
 * @param varianceFactor - Random variance factor (0-1) for realism
 * @returns Grid export (kWh), typically ~15-20% of PV
 */
export function calculateGridExport(pvKwh: number, varianceFactor: number = 0.5): number {
  // Base export: ~18% of PV
  const basePercent = ENERGY_CONSTANTS.GRID_EXPORT_BASE_PERCENT / 100;
  // Apply variance: ±15%
  const variance = (varianceFactor - 0.5) * 2 * ENERGY_CONSTANTS.GRID_EXPORT_VARIANCE;
  const exportPercent = Math.max(0.1, basePercent + variance); // Minimum 10%
  return Math.round(pvKwh * exportPercent * 10) / 10;
}
